export type ListingFilters = {
  brandIds: string[];
  inStock: boolean;
  onSale: boolean;
  priceMin: number | null;
  priceMax: number | null;
};

export type ListingFilterSearch = {
  brand?: string;
  stock?: boolean;
  sale?: boolean;
  min?: number;
  max?: number;
};

export const EMPTY_LISTING_FILTERS: ListingFilters = {
  brandIds: [],
  inStock: false,
  onSale: false,
  priceMin: null,
  priceMax: null,
};

function parseFlag(value: unknown): boolean {
  return value === true || value === 1 || value === "1" || value === "true";
}

function parsePrice(value: unknown): number | null {
  if (value === undefined || value === null || value === "") return null;
  const price = Number(value);
  return Number.isFinite(price) && price >= 0 && price <= 1_000_000 ? price : null;
}

export function normalizeListingFilters(value: unknown): ListingFilters {
  const input = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const rawBrandIds = Array.isArray(input.brandIds)
    ? input.brandIds
    : String(input.brand ?? "").split(",");
  const brandIds = Array.from(
    new Set(
      rawBrandIds
        .map((id) => String(id).trim())
        .filter((id) => /^[a-z\d-]{1,64}$/i.test(id)),
    ),
  ).slice(0, 30);
  return {
    brandIds,
    inStock: parseFlag(input.inStock ?? input.stock),
    onSale: parseFlag(input.onSale ?? input.sale),
    priceMin: parsePrice(input.priceMin ?? input.min),
    priceMax: parsePrice(input.priceMax ?? input.max),
  };
}

export function listingFiltersFromSearch(input: unknown): ListingFilters {
  return normalizeListingFilters(input);
}

export function hasActiveListingFilters(filters: ListingFilters): boolean {
  return (
    filters.brandIds.length > 0 ||
    filters.inStock ||
    filters.onSale ||
    filters.priceMin !== null ||
    filters.priceMax !== null
  );
}

export function listingFilterSearch(filters: ListingFilters): ListingFilterSearch {
  return {
    brand: filters.brandIds.length ? filters.brandIds.join(",") : undefined,
    stock: filters.inStock ? true : undefined,
    sale: filters.onSale ? true : undefined,
    min: filters.priceMin ?? undefined,
    max: filters.priceMax ?? undefined,
  };
}

export function listingFiltersKey(filters: ListingFilters): string {
  return JSON.stringify(normalizeListingFilters(filters));
}

export function listingFiltersPath(filters: ListingFilters): string {
  return hasActiveListingFilters(filters)
    ? `/filters/${encodeURIComponent(listingFiltersKey(filters))}`
    : "";
}

export function buildListingPageHref(
  baseUrl: string,
  page: number,
  sort: string | null | undefined,
  filters: ListingFilters,
): string {
  const url = new URL(baseUrl, "https://magnum-fs.com");
  const params = url.searchParams;
  if (page > 1) params.set("page", String(page));
  else params.delete("page");
  if (sort && sort !== "default") params.set("sort", sort);
  else params.delete("sort");
  for (const key of ["brand", "stock", "sale", "min", "max"]) params.delete(key);
  const search = listingFilterSearch(filters);
  for (const [key, value] of Object.entries(search)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const query = params.toString();
  return `${url.pathname}${query ? `?${query}` : ""}`;
}
